import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { PoModalAction, PoModalComponent } from '@po-ui/ng-components';

import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { getApiErrorMessage } from '../../../core/utils/http-error-message.util';
import { FeedbackResolution, ReviewFeedback } from '../../interfaces/review-feedback.interface';
import { FINDING_RESOLUTIONS, OVERALL_RESOLUTIONS, ResolutionOption } from '../../review-feedback.constants';
import { ReviewService } from '../../review.service';

/**
 * As avaliações de outros usuários sobre um achado (ou sobre o review), só para leitura.
 *
 * "Outras avaliações", e não "equipe": o review é compartilhado com a área, que não é
 * necessariamente o time de quem olha. Fica separado de "Sua avaliação" de propósito: a
 * avaliação é de quem a deu, e a curadoria a lê como dele — quem vê não troca (registro 0036
 * do backend). Admin pode remover, com confirmação: o servidor diz quem pode em `removable`.
 * Segue o desenho de "Sua avaliação": mesmo título de seção, mesmas cores de resolução. A
 * lista fica sempre à vista — quase sempre são uma ou duas avaliações, e escondê-las atrás de
 * um clique tirava justamente o que o dev veio ver; acima de `visibleLimit`, "Ver mais".
 */
@Component({
  selector: 'app-review-other-feedback',
  templateUrl: './review-other-feedback.component.html',
  styleUrls: ['./review-other-feedback.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewOtherFeedbackComponent {
  @Input() feedbacks: ReviewFeedback[] = [];
  /** `overall` usa os rótulos do veredito geral ("Review preciso"...); achado, os do achado. */
  @Input() overall = false;
  @Output() removed = new EventEmitter<ReviewFeedback>();

  /** Quantas avaliações aparecem antes do "Ver mais". */
  readonly visibleLimit = 2;
  showAll = false;
  removingId: string | null = null;
  /** A avaliação que aguarda confirmação de remoção. */
  pendingRemoval: ReviewFeedback | null = null;

  @ViewChild('removeModal') removeModal?: PoModalComponent;

  removePrimaryAction: PoModalAction = {
    label: 'Remover',
    danger: true,
    action: () => this.remove()
  };
  removeSecondaryAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.closeRemoveModal()
  };

  constructor(
    private reviewService: ReviewService,
    private notification: NotificationServiceHM,
    private cdr: ChangeDetectorRef
  ) {}

  get options(): ResolutionOption[] {
    return this.overall ? OVERALL_RESOLUTIONS : FINDING_RESOLUTIONS;
  }

  /** Mais recente primeiro: é o que se disse por último sobre o achado. */
  get sorted(): ReviewFeedback[] {
    return [...this.feedbacks].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  }

  get visible(): ReviewFeedback[] {
    return this.showAll ? this.sorted : this.sorted.slice(0, this.visibleLimit);
  }

  get hiddenCount(): number {
    return Math.max(0, this.feedbacks.length - this.visibleLimit);
  }

  toggleShowAll(): void {
    this.showAll = !this.showAll;
  }

  optionOf(resolution: FeedbackResolution): ResolutionOption | undefined {
    return this.options.find(option => option.value === resolution);
  }

  /**
   * Remover a avaliação de outra pessoa é definitivo: pede confirmação nomeando de quem é.
   * po-modal `sm`, como a exclusão de review: o `PoDialogService.confirm` abre largo demais
   * para uma frase.
   */
  confirmRemove(feedback: ReviewFeedback): void {
    if (!feedback.removable || this.removingId) { return; }
    this.pendingRemoval = feedback;
    this.setRemoveLoading(false);
    this.removeModal?.open();
  }

  closeRemoveModal(): void {
    if (this.removingId) { return; }
    this.pendingRemoval = null;
    this.removeModal?.close();
  }

  trackById(_index: number, fb: ReviewFeedback): string {
    return fb.id;
  }

  private remove(): void {
    const feedback = this.pendingRemoval;
    if (!feedback || this.removingId) { return; }
    this.removingId = feedback.id;
    this.setRemoveLoading(true);
    this.cdr.markForCheck();
    this.reviewService.deleteReviewFeedback(feedback.resourceType, feedback.resourceId, feedback.id).subscribe({
      next: () => {
        this.removingId = null;
        this.setRemoveLoading(false);
        this.closeRemoveModal();
        this.notification.showToast('Avaliação removida', 'success');
        this.removed.emit(feedback);
        this.cdr.markForCheck();
      },
      error: (error: unknown) => {
        this.removingId = null;
        this.setRemoveLoading(false);
        this.notification.showToast(getApiErrorMessage(error, 'Não foi possível remover a avaliação.'), 'error');
        this.cdr.markForCheck();
      }
    });
  }

  private setRemoveLoading(loading: boolean): void {
    this.removePrimaryAction = { ...this.removePrimaryAction, loading, label: loading ? 'Removendo...' : 'Remover' };
    this.removeSecondaryAction = { ...this.removeSecondaryAction, disabled: loading };
  }
}
